import { CurrentUser } from "@/types/user"
import api from "@/services/api"
import { Folder, FolderItems, FolderType } from "@/types/folder"
import _, { get } from "lodash"
import { folderMixin } from "@/mixins/folderMixin"
import { Schedule, SortByOptions } from "@/types/schedule"
import { Field, Team, Template } from "@/types"

export default class FolderService {
	public currentUser: CurrentUser | null = null
	public folders: { [title:string]: Folder } = {}
    public currentSubFolderList: Folder[] = []
	public currentSubFolderListItemCheck: Folder[] = []
	public currentfolderItemsSubFolderList: any[] = []
    public folderList: Folder[] = []
    public folderItemGroups:FolderItems[] = []
	private folderType!: FolderType

	// private folderItemGroups: folderItems[] = [];
	public getPermittedFolders () : { [title:string] : Folder } {
		const userTeamIds = this.currentUser!.teams.map(t => t.id)
		// folders where some of the user's teams are included in the folder's teams
		const permittedFolders = _.pickBy(this.folders, folder =>
			(!folder.teams || folder.teams.length === 0) ||
			folder.teams.some(folderTeamId => userTeamIds.includes(folderTeamId)))
		return permittedFolders
	}

	// needs a hack like this somewhere as saving will default to saving the folder.id to Item.folder, but it expects the folder title
	public getPermittedSelectionFolders () : {id: string, title: string }[] {
		const permittedFolders: { [title:string] : Folder } = this.getPermittedFolders()

		const folderList: Folder[] = Object.entries(permittedFolders).map(([key, value]) => value)
		const objectList: {id: string, title: string}[] = Object.values(permittedFolders).map(folder => {
			// Get the full path of the folder
			const folderTitle = folderMixin.methods.getPathToRootFolder(folder, folderList, true)
			return { id: folderTitle, title: folderTitle }
		})
		// Return the sorted list
		return objectList.sort(function (a, b) {
			return a.title.localeCompare(b.title)
		})
	}

	friendlyFolderName (item: {[key:string]:string}) {
		if (item.folder === null || item.folder === undefined) {
			return "None"
		}
		return item.folder
	}

	public async load (currentUser: CurrentUser, folderType: FolderType) {
		const currentUserLoad = currentUser ? Promise.resolve(this.currentUser = currentUser) : api.me().then(user => this.currentUser = user)
		this.folderType = folderType
		const foldersLoad = this.loadFolders()

		await Promise.all([foldersLoad, currentUserLoad])
	}

	public async loadFolders () {
		const refreshedFolders: Folder[] = await api.foldersGet(this.folderType)
		refreshedFolders.forEach(refreshedFolder => {
			const existingFolder = this.folders[refreshedFolder.title]
			if (existingFolder) {
				refreshedFolder.expanded = existingFolder.expanded
			} else {
				refreshedFolder.expanded = true
			}
		})

		this.folderList = refreshedFolders
		this.folders = refreshedFolders.toDictionary("title")
	}

	public loadFolderItemGroups (items:any) {
		const permittedFolders = this.getPermittedFolders()
		// Get all items that are either in a root folder or are not in a folder
		const folderItemGroups = _(items)
			.filter(c => !c.folder || ((permittedFolders[(c.folder)] !== undefined) && !permittedFolders[(c.folder)].parentFolderId))
			.groupBy(c => c.folder || "")
			.map((groupedItems: any, folderName: string) => {
				const hasNoFolder = folderName === null || folderName === undefined || folderName === ""

				const folderItems: FolderItems = {
					id: folderName,
					folderTitle: hasNoFolder ? "Uncategorized" : folderName,
					expanded: hasNoFolder || !!this.folders[folderMixin.methods.getFolderNameFromPath(folderName)].expanded,
					isFolderType: true,
					items: groupedItems,
					allowRowClick: true,
					onlyEmitOnRowClick: true,
					subFolders: []
				}

				// Reset the sub folder list
				this.currentSubFolderList = []

				var folder = folderMixin.methods.getFolderFromPath(folderName, this.folderList)
				if (folder) {
					if (this.subFolderContainsItem(folder, items)) {
						this.buildSubFolderList(folder.id)
						this.buildSubFolderitemList(folderItems, folder, items)
					}
				}

				return folderItems
			}).value()

		// Add any empty root folders that have a sub folder containing a Item

		this.folderItemGroups = folderItemGroups

		this.addValidEmptyRootFolders(permittedFolders, items)
	}

	public tableRows () {
		const tableRows = _(this.folderItemGroups)
			.orderBy([(item: any) => { return item.folderTitle }])
			.map(folderGroup => {
				if (folderGroup.expanded) {
					const rootFolderGroup = folderGroup
					const rootfolderItems = folderGroup.items

					// Reset the list of sub folders
					this.currentfolderItemsSubFolderList = []

					if (folderGroup.subFolders && folderGroup.subFolders.length > 0) {
						// Sub folders exist - build up the list of sub folders & Items
						this.buildfolderItemsSubFolderList(folderGroup, this.getItemOrdering, this.getItemOrderDirection)

						return [rootFolderGroup,
							..._(rootfolderItems).orderBy(this.getItemOrdering, this.getItemOrderDirection).value(),
							..._(this.currentfolderItemsSubFolderList).value()]
					} else {
						// No sub folders
						return [folderGroup, ..._(folderGroup.items).orderBy(this.getItemOrdering, this.getItemOrderDirection).value()]
					}
				} else {
					return [folderGroup]
				}
			}).flatten()
			.value()
		return tableRows
	}

	subFolderContainsItem (folder: Folder, items:(Schedule|Field|Template|Team)[]) {
		// Rebuild the sub folder list for the current folder
		this.currentSubFolderListItemCheck = []
		this.buildSubFolderListItemCheck(folder.id)

		// Folder has no sub folders
		if (this.currentSubFolderListItemCheck.length < 1) {
			return false
		}

		// Check if any sub folders contain a Item
		if (items.some(v => this.currentSubFolderListItemCheck.find(f => folderMixin.methods.getPathToRootFolder(f, this.folderList, true) === v.folder))) {
			return true
		} else {
			return false
		}
	}

	// Loops through to recursively build up a list of sub folder items for the provided parent folder items
	buildSubFolderitemList (parentFolderItem: FolderItems, parentFolder: Folder, items:(Schedule|Field|Template|Team)[]) {
		// Filter the folders list to only include folders that have a parent
		const itemFoldersFiltered: Folder[] = this.folderList.filter(f => f.parentFolderId && f.parentFolderId !== null && f.id !== parentFolder.id)

		itemFoldersFiltered.forEach(folder => {
			// Get the full path to root for the current folder
			let folderPath: string = folderMixin.methods.getPathToRootFolder(folder, this.folderList, true)

			if (this.isDirectSubFolder(folderPath, parentFolder)) {
				// Get the list of items for the current folder
				var itemList = items.filter(s => !!s.folder && folderMixin.methods.getFolderNameFromPath(s.folder) === folder.title)

				// If the current folder contains a Item, push it to be displayed
				if (itemList.some(v => v.folder === folderPath)) {
					const folderItems: FolderItems = {
						folderTitle: folderMixin.methods.getPathToRootFolder(folder, this.folderList, true),
						expanded: !!this.folders[folderMixin.methods.getFolderNameFromPath(folder.title)].expanded, // items with no folder are always expanded
						isFolderType: true,
						items: itemList,
						allowRowClick: true,
						onlyEmitOnRowClick: true,
						subFolders: [],
						parentFolderId: parentFolder.id
					}

					// Add the folder Item to the parent's list of sub folders
					if (!parentFolderItem.subFolders) {
						parentFolderItem.subFolders = []
					}
					parentFolderItem.subFolders.push(folderItems)

					// Check if the folder Item has any sub folders that need to be displayed
					this.buildSubFolderitemList(folderItems, folder, items)
				} else {
					// Check if the folder has any sub folders that contain a Item
					if (this.subFolderContainsItem(folder, items)) {
						const folderItems: FolderItems = {
							folderTitle: folderMixin.methods.getPathToRootFolder(folder, this.folderList, true),
							expanded: !!this.folders[folderMixin.methods.getFolderNameFromPath(folder.title)].expanded, // items with no folder are always expanded
							isFolderType: true,
							items: itemList,
							allowRowClick: true,
							onlyEmitOnRowClick: true,
							subFolders: [],
							parentFolderId: parentFolder.id
						}

						// Add the folder Item to the parent's list of sub folders
						if (!parentFolderItem.subFolders) {
							parentFolderItem.subFolders = []
						}
						parentFolderItem.subFolders.push(folderItems)

						// Check if the folder Item has any sub folders that need to be displayed
						this.buildSubFolderitemList(folderItems, folder, items)
					}
				}
			}
		})
	}

	// Adds any root folders that do not directly contain a Item, but have a sub folder that contains a Item
	addValidEmptyRootFolders (permittedFolders: { [title: string]: Folder }, items:(Schedule|Field|Template|Team)[]) {
		const filteredFolderList = _(this.folderList)
			.filter(f => ((permittedFolders[(f.title)] !== undefined) && !permittedFolders[(f.title)].parentFolderId))

		filteredFolderList.forEach(folder => {
			if (!items.some(s => s.folder === folder.title) && this.subFolderContainsItem(folder, items)) {
				// Create folder Item
				const folderItems: FolderItems = {
					folderTitle: folder.title,
					expanded: !!this.folders[folderMixin.methods.getFolderNameFromPath(folder.title)].expanded,
					isFolderType: true,
					items: [],
					allowRowClick: true,
					onlyEmitOnRowClick: true,
					subFolders: []
				}

				// Reset the sub folder list
				this.currentSubFolderList = []

				// Build the sub folder list for the current folder
				this.buildSubFolderList(folder.id)

				// Build the Item lists for all of the current folder's sub folders
				this.buildSubFolderitemList(folderItems, folder, items)

				// Add the folder Item to the main list of folder items
				this.folderItemGroups.push(folderItems)
			}
		})
	}

	buildSubFolderListToArray (folderId: string, addToList: (subFolder: any) => void) {
		// Create list of direct sub folders of provided folder ID
		let subFolderList: Folder[] = this.folderList.filter(f => f.parentFolderId === folderId)
		subFolderList.forEach(item => {
			addToList(item)
			this.buildSubFolderListToArray(item.id, addToList)
		})
	}

	buildSubFolderList (folderId: string) {
		this.buildSubFolderListToArray(folderId, item => this.currentSubFolderList.push(item))
	}

	// Builds up the list of sub folders and items
	public buildfolderItemsSubFolderList (folderItems: FolderItems, getItemOrdering:((item: any) => any)[], getItemOrderDirection: ("asc" | "desc")[]) {
		if (folderItems.expanded) {
			if (folderItems.subFolders && folderItems.subFolders.length > 0) {
				// Loop through each sub folder, recursively getting any direct sub folders
				for (let i = 0, count = folderItems.subFolders.length; i < count; i++) {
					// Add sub folder to the list
					this.currentfolderItemsSubFolderList.push(folderItems.subFolders[i])
					let itemList: any[] = []
					// Add each Item for the current sub folder if the folder is expanded
					if (folderItems.subFolders[i].expanded) {
						folderItems.subFolders[i].items.forEach((item: any) => {
							itemList.push(item)
						})
						// Order the list of items and push it to the master list
						const itemListCopy: any[] = _(itemList).orderBy(getItemOrdering, getItemOrderDirection).value()
						this.currentfolderItemsSubFolderList.push(...itemListCopy)
					}

					// Get direct sub folders of current sub folder
					this.buildfolderItemsSubFolderList(folderItems.subFolders[i], getItemOrdering, getItemOrderDirection)
				}
			}
		}
	}

	buildSubFolderListItemCheck (folderId: string) {
		this.buildSubFolderListToArray(folderId, item => this.currentSubFolderListItemCheck.push(item))
	}

	private isDirectSubFolder (folderName: string, parentFolder: Folder): boolean {
		const folder = folderMixin.methods.getFolderFromPath(folderName, this.folderList)

		if (!folder) return false

		if (this.folderList.firstOrDefault(f => f.title === parentFolder.title && f.id === folder.parentFolderId)) {
			return true
		} else {
			return false
		}
	}

	public get getItemOrdering () : ((item: any) => any)[] {
		return this.sortByOptions.map((sortByOption) => {
			return (item: any) => { return get(item, sortByOption.columnKey, 0) }
		})
	}

	public get getItemOrderDirection () : ("asc" | "desc")[] {
		return this.sortByOptions.map((sortByOption) => {
			return sortByOption.descending
		})
	}

	private sortByOptions: SortByOptions[] = this.getDefaultSortByOptions()

	private getDefaultSortByOptions () : SortByOptions[] {
		return [
			{
				columnKey: "status",
				descending: "asc"
			},
			{
				columnKey: "title",
				descending: "asc"
			}
		]
	}

	public folderPathName (folderTitle: string) {
		if (this.folderList.filter(f => f.title === folderTitle).length === 0) {
			return folderTitle
		}

		const folder: Folder = folderMixin.methods.getFolderFromPath(folderTitle, this.folderList)
		return folderMixin.methods.getPathToRootFolder(folder, this.folderList, true)
	}

	public isParentFolder (folderTitle: string, templateFolder ?: string|null) {
		if (!!templateFolder && templateFolder?.includes("\\")) {
			const folderPaths = templateFolder.split("\\")
			if (folderPaths[folderPaths.length - 1] === folderTitle) {
				return false
			}
			return true
		}
		return true
	}

	public getFolderLevel (folderTitle: string) {
		if (!folderTitle) return 0
		const path = this.folderPathName(folderTitle)
		return path.match(/\\/g)?.length ?? 0
	}

	public getFolderNameFromPath (folderPath: string) {
		if (!folderPath) return ""
		const split = folderPath.split("\\")
		return split[split.length - 1]
	}

	public getShortenedPathPrefix (folderPath: string): string {
		const split = folderPath.split("\\")
		if (split.length < 2) {
			return ""
		}
		const symbolicLevel = "...\\"
		return `${split.length === 2 ? "" : symbolicLevel}${split[split.length - 2]}\\`
	}

	public getFolderTooltipText (folderPath: string) {
		return `'${folderPath.replace(/\\/g, "\n\\")}'`
	}
}
